import type { TLocalStorageDomainNamespace } from "../types";

function createNamespacedKey(base: string, key: string) {
  return `${base}__${key}`;
}

function createWriteSetter<TSubdomainKeys extends string>(
  base: TLocalStorageDomainNamespace,
) {
  return <TCastedInput>(key: TSubdomainKeys, value: TCastedInput) => {
    const namespacedKey = createNamespacedKey(base, key);
    const stringifiedValue = JSON.stringify(value);

    localStorage.setItem(namespacedKey, stringifiedValue);
  };
}

function createReadGetter<TSubdomainKeys extends string>(
  base: TLocalStorageDomainNamespace,
) {
  return <TCastedReturn>(
    key: TSubdomainKeys,
    fallbackDefault: TCastedReturn,
  ) => {
    const namespacedKey = createNamespacedKey(base, key);
    const result = localStorage.getItem(namespacedKey);

    if (!result) {
      const stringifiedFallback = JSON.stringify(fallbackDefault);
      localStorage.setItem(namespacedKey, stringifiedFallback);
      return fallbackDefault;
    }

    return JSON.parse(result) as TCastedReturn;
  };
}

/**
 * @typeparam `TSubdomainKeys` is expected to be union of string literals that represent the keys that are for the given namespace
 *
 * @description
 * - class that enables to create a namespace for localStorage
 * - it further provides a method to create a key handler for a specific key that allows to read and write the given key in a type-safe manner inside the namespace
 * - because slidesync runs both of these apps on the same domain, it is necessary to separate the localStorage keys in order to avoid conflicts / keep it more organized
 * - this is used as a baseline constructor for a hook to expose the localStorage keys in a type-safe manner
 */
export default class LocalStorageNamespace<TSubdomainKeys extends string> {
  private base: TLocalStorageDomainNamespace;

  private read: ReturnType<typeof createReadGetter<TSubdomainKeys>>;

  private write: ReturnType<typeof createWriteSetter<TSubdomainKeys>>;

  constructor(base: TLocalStorageDomainNamespace) {
    this.base = base;
    this.read = createReadGetter<TSubdomainKeys>(this.base);
    this.write = createWriteSetter<TSubdomainKeys>(this.base);
  }

  /**
   * @typeparam `TKeyValue` is the type of the value that is stored in the localStorage for the given key. It can be a primitive type or an object types, will be stringified before storing and parsed when reading with JSON.stringify and JSON.parse
   * @param key - the key that is used to store the value in the localStorage, will be namespaced with the base
   * @param initValue - the default value that is used if the key is not present in the localStorage
   */
  createKeyHandler = <TKeyValue>(key: TSubdomainKeys, initValue: TKeyValue) => {
    return {
      /**
       * - will read the value from the localStorage for the given key
       * - if the key is not present in the localStorage, it will write the initValue to the localStorage and return it
       */
      read: () => this.read<TKeyValue>(key, initValue),
      /**
       * - allows to write a new value to the localStorage for the given key
       * - is NOT connected to react state, so it will not trigger a rerender
       */
      write: (value: TKeyValue) => {
        this.write<TKeyValue>(key, value);
      },
    };
  };
}
